import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaTimes, FaSearch, FaChevronDown } from 'react-icons/fa';
import { getAccessToken, getNativeWebview } from '../react-queries/queries';
import { processApiResponse } from '../common/errorMessage';
import {getBookcommunityApplicants, approveToBookcommunity, rejectToBookcommunity} from '../api/BookcommunityApi';
import { CommunityApplicantStatus, communityApplicantStatus2text } from '../common/enums';
import { ERROR_CODES } from '../common/errorMessage';
import { API_URL } from '../config';
import { launchUrl } from '../api/native/NativeApis';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 480px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #222;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f7;
  }
`;

const FilterSection = styled.div`
  padding: 8px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;


const FilterInput = styled.input`
  flex: 1 1 0;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #222;
  background: #fff;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
  
  &::placeholder {
    color: #bbb;
  }
`;


const SearchButton = styled.button`
  padding: 6px 8px;
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  min-width: 65px;
  gap: 4px;
  
  &:hover {
    background: #0056CC;
  }
`;

const ApplicantList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px;
`;

const ApplicantItem = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ApplicantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ApplicantMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const NamecardModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const NamecardModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 95%;
  max-width: 480px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const NamecardModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const NamecardModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #222;
`;

const NamecardModalBody = styled.div`
  flex: 1;
  min-height: 0;
`;

const NamecardIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
`;

const OpenExternalButton = styled.button`
  margin-left: 8px;
  padding: 6px 12px;
  background: #244AC2;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
`;

const NamecardHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ProfileImg = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eaeaea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #666;
`;

const ApplicantDetails = styled.div`
  flex: 1;
`;

const ApplicantName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #222;
  margin-bottom: 2px;
`;

const ApplicantId = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 2px;
`;

const ApplicantEmail = styled.div`
  font-size: 14px;
  color: #666;
`;

const StatusBadge = styled.div`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  min-width: 60px;
  
  ${props => {
    switch(props.status) {
      case 'APPLIED':
        return 'background: #fff3cd; color: #856404;';
      case 'APPROVED':
        return 'background: #d4edda; color: #155724;';
      case 'REJECTED':
        return 'background: #f8d7da; color: #721c24;';
      case 'CANCELLED':
        return 'background: #f8f9fa; color: #6c757d;';
      case 'WITHDRAWN':
        return 'background: #f8f9fa; color: #6c757d;';
      default:
        return 'background: #f8f9fa; color: #6c757d;';
    }
  }}
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
  font-size: 14px;
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
`;
/*
const getStatusText = (status) => {
  communityApplicantStatus2text(status);
};
*/
const FilterSelect = styled.div`
  position: relative;
  flex: 1;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #222;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
`;

const SelectList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectItem = styled.div`
  padding: 4px 8px;
  font-size: 14px;
  color: #222;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f7;
  }
  
  &.selected {
    background: #e3f2fd;
    color: #007AFF;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  margin-top: 0;
`;

const ActionButton = styled.button`
  padding: 4px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &.accept {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
  
  &.reject {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
`;

/*
const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  align-items: center;
`;
*/

const StatusMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 12px;
  border: 1px solid #c3e6cb;
  animation: fadeInOut 10s ease-in-out;
  
  @keyframes fadeInOut {
    0% { opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { opacity: 0; }
  }
`;

export default function CommunityMoimApplicantPage({ bookCommunityId, onClose }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalResult, setTotalResult] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    loginid: '',
    name: '',
    email: '',
    applicantStatus: ''
  });
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [updatingApplicant, setUpdatingApplicant] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [showNamecardModal, setShowNamecardModal] = useState(false);
  const [namecardUrl, setNamecardUrl] = useState('');
  const [namecardTitle, setNamecardTitle] = useState('');

  const getApplicantStatusText = (applicantStatus) => {
    if(applicantStatus == CommunityApplicantStatus.APPROVED) return '회원';
    return communityApplicantStatus2text(applicantStatus);
  };

  const loadApplicants = useCallback(async (reset = false) => {
    if (loading || (!hasMore && !reset)) return;
    
    setLoading(true);
    try {
      const {accessToken} = await getAccessToken();
      const currentPage = reset ? 0 : page;
      const size = 50;
      
      const response = await getBookcommunityApplicants(
        accessToken || '', 
        bookCommunityId,   
        filters.loginid || null,
        filters.name || null,
        filters.email || null,
        filters.applicantStatus || null,
        currentPage,
        size
      );
      
      const processedResponse = processApiResponse(response);
      
      if (processedResponse.success) {
        const newApplicants = processedResponse.data || [];

        setTotalResult(processedResponse.page.total);
        
        if (reset) {
          setApplicants(newApplicants);
        } else {
          setApplicants(prev => [...prev, ...newApplicants]);
        }
        
        setHasMore(newApplicants.length === size);
        setPage(currentPage + 1);
      }
    } catch (error) {
      console.error('Error loading applicants:', error);
    } finally {
      setLoading(false);
    }
  }, [bookCommunityId, page, loading, hasMore, filters]);

  useEffect(() => {
    loadApplicants(true);
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatusSelect = (status) => {
    setFilters(prev => ({
      ...prev,
      applicantStatus: status
    }));
    setShowStatusDropdown(false);
  };

  const handleSearch = () => {
    setPage(0);
    setHasMore(true);
    loadApplicants(true);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
      loadApplicants();
    }
  };

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showStatusDropdown && !e.target.closest('.status-select')) {
        setShowStatusDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusDropdown]);

  // 지원자 선정 처리
  const handleApproveApplicant = async (applicantId) => {
    if (updatingApplicant === applicantId) return;
    
    setUpdatingApplicant(applicantId);
    try {
      const {accessToken} = await getAccessToken();
      const response = await approveToBookcommunity(accessToken || '', applicantId, bookCommunityId);
      const processedResponse = processApiResponse(response);
      
      if (processedResponse.success) {
        // 지원자 목록에서 해당 지원자의 상태 업데이트
        setApplicants(prev => prev.map(applicant => 
          applicant.userId === applicantId 
            ? { ...applicant, applicantStatus: processedResponse.data.applicantStatus }
            : applicant
        ));
      } else if (response.errorCode === ERROR_CODES.OVERLIMIT) {
        showStatusMessage('모집인원이 초과되었습니다.');
      } else {
        showStatusMessage(processedResponse.errorMessage || '신청에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error accepting applicant:', error);
    } finally {
      setUpdatingApplicant(null);
    }
  };

  // 지원자 탈락 처리
  const handleRejectApplicant = async (applicantId) => {
    if (updatingApplicant === applicantId) return;
    
    setUpdatingApplicant(applicantId);
    try {
      const {accessToken} = await getAccessToken();
      const response = await rejectToBookcommunity(accessToken || '', applicantId, bookCommunityId);
      const processedResponse = processApiResponse(response);
      
      if (processedResponse.success) {
        // 지원자 목록에서 해당 지원자의 상태 업데이트
        setApplicants(prev => prev.map(applicant => 
          applicant.userId === applicantId 
            ? { ...applicant, applicantStatus: processedResponse.data.applicantStatus }
            : applicant
        ));
      }
    } catch (error) {
      console.error('Error rejecting applicant:', error);
    } finally {
      setUpdatingApplicant(null);
    }
  };

  // 지원자 상태별 액션 버튼 렌더링
  const renderActionButtons = (applicant) => {
    const { userId, applicantStatus } = applicant;
    const isUpdating = updatingApplicant === userId;

    switch (applicantStatus) {
      case 'APPROVED':
        return (
          <ActionButtons>
            <ActionButton 
              className="reject"
              onClick={() => handleRejectApplicant(userId)}
              disabled={isUpdating}
            >
              {isUpdating ? '처리중...' : '퇴출'}
            </ActionButton>
          </ActionButtons>
        );
        
      case 'REJECTED':
        return (
          <ActionButtons>
            <ActionButton 
              className="accept"
              onClick={() => handleApproveApplicant(userId)}
              disabled={isUpdating}
            >
              {isUpdating ? '처리중...' : '선정'}
            </ActionButton>
          </ActionButtons>
        );
        
      case 'APPLIED':
        return (
          <ActionButtons>
            <ActionButton 
              className="accept"
              onClick={() => handleApproveApplicant(userId)}
              disabled={isUpdating}
            >
              {isUpdating ? '처리중...' : '선정'}
            </ActionButton>
            <ActionButton 
              className="reject"
              onClick={() => handleRejectApplicant(userId)}
              disabled={isUpdating}
            >
              {isUpdating ? '처리중...' : '탈락'}
            </ActionButton>
          </ActionButtons>
        );
        
      default:
        return null;
    }
  };

  const showStatusMessage = (message) => {
    setStatusMessage(message);
    setTimeout(() => {
      setStatusMessage('');
    }, 10000); // 10초 후 메시지 제거
  };

  const handleApplicantMainInfoClick = (applicant) => {
    if (!applicant?.uuid) {
      alert('네임카드 정보가 없습니다.');
      return;
    }

    setNamecardTitle(applicant.name || '네임카드');
    setNamecardUrl(`${API_URL}/open/v1/user/namecard/${applicant.uuid}`);
    setShowNamecardModal(true);
  };

  const closeNamecardModal = () => {
    setShowNamecardModal(false);
    setNamecardUrl('');
    setNamecardTitle('');
  };

  const openNamecardExternal = () => {
    if (!namecardUrl) return;
    if (getNativeWebview()) {
      launchUrl(namecardUrl);
    } else {
      window.open(namecardUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Modal>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>지원자 목록 ({totalResult})</ModalTitle>
          <CloseButton onClick={() => onClose(false)}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>
        
        <FilterSection>
          <FilterRow>
            <FilterInput
              placeholder="로그인 ID"
              value={filters.loginid}
              onChange={(e) => handleFilterChange('loginid', e.target.value)}
            />
            <FilterInput
              placeholder="이름"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
            />
          </FilterRow>
          <FilterRow>
            <FilterInput
              placeholder="이메일"
              value={filters.email}
              onChange={(e) => handleFilterChange('email', e.target.value)}
            />
            <FilterSelect className="status-select">
              <SelectButton onClick={() => setShowStatusDropdown(!showStatusDropdown)}>
                {filters.applicantStatus ? communityApplicantStatus2text(filters.applicantStatus) : '상태 선택'}
                <FaChevronDown size={12} />
              </SelectButton>
              {showStatusDropdown && (
                <SelectList>
                  <SelectItem
                    onClick={() => handleStatusSelect('')}
                    className={!filters.applicantStatus ? 'selected' : ''}
                  >
                    전체
                  </SelectItem>
                  {Object.values(CommunityApplicantStatus).map((status) => (
                    <SelectItem
                      key={status}
                      onClick={() => handleStatusSelect(status)}
                      className={filters.applicantStatus === status ? 'selected' : ''}
                    >
                      {communityApplicantStatus2text(status)}
                    </SelectItem>
                  ))}
                </SelectList>
              )}
            </FilterSelect>
          </FilterRow>
          <FilterRow>
            <SearchButton onClick={handleSearch}>
              <FaSearch size={14} />
              검색
            </SearchButton>
            {statusMessage && <StatusMessage>{statusMessage}</StatusMessage>}
          </FilterRow>
        </FilterSection>
        
        <ApplicantList onScroll={handleScroll}>
          {applicants.length === 0 && !loading ? (
            <EmptyText>지원자가 없습니다.</EmptyText>
          ) : (
            applicants.map((applicant, index) => (
              <ApplicantItem key={applicant.userId || index}>
                <ApplicantInfo>
                  <ApplicantMainInfo
                    $clickable={!!applicant.uuid}
                    onClick={() => handleApplicantMainInfoClick(applicant)}
                  >
                    <ProfileImg>
                      {applicant.name?.charAt(0) || '?'}
                    </ProfileImg>
                    <ApplicantDetails>
                      <ApplicantName>{applicant.name || '-'}</ApplicantName>
                      <ApplicantId>ID: {applicant.loginid || '-'}</ApplicantId>
                      <ApplicantEmail>{applicant.email || '-'}</ApplicantEmail>
                    </ApplicantDetails>
                  </ApplicantMainInfo>
                  <StatusBadge status={applicant.applicantStatus}>
                    {getApplicantStatusText(applicant.applicantStatus)}
                  </StatusBadge>
                </ApplicantInfo>
                {renderActionButtons(applicant)}
              </ApplicantItem>
            ))
          )}
          
          {loading && (
            <LoadingText>로딩 중...</LoadingText>
          )}
        </ApplicantList>
      </ModalContent>

      {showNamecardModal && namecardUrl && (
        <NamecardModalOverlay onClick={closeNamecardModal}>
          <NamecardModalContent onClick={(e) => e.stopPropagation()}>
            <NamecardModalHeader>
              <NamecardModalTitle>{namecardTitle}</NamecardModalTitle>
              <NamecardHeaderActions>
                <OpenExternalButton onClick={openNamecardExternal}>외부에서 열기</OpenExternalButton>
                <CloseButton onClick={closeNamecardModal}>
                  <FaTimes />
                </CloseButton>
              </NamecardHeaderActions>
            </NamecardModalHeader>
            <NamecardModalBody>
              <NamecardIframe src={namecardUrl} title={namecardTitle} />
            </NamecardModalBody>
          </NamecardModalContent>
        </NamecardModalOverlay>
      )}
    </Modal>
  );
}
